import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiChevronRight } from 'react-icons/fi';
import { matchNavItem } from './navigation';
import { cn } from '../ui/cn';

const BreadcrumbContext = createContext({ extras: [], setExtras: () => {} });

/**
 * BreadcrumbProvider — holds a list of "extra" crumbs pages can push
 * (e.g. a detail page can add "Acme Corp" on top of "Partners").
 *
 * Mounted once in Shell.
 */
export function BreadcrumbProvider({ children }) {
  const [extras, setExtras] = useState([]);
  const value = useMemo(() => ({ extras, setExtras }), [extras]);
  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
}

/**
 * usePageCrumbs — pages call this to push extra crumbs while mounted.
 *
 *   usePageCrumbs([{ label: partner.company_name }]);
 *
 * Crumbs are cleared automatically when the page unmounts.
 */
export function usePageCrumbs(crumbs) {
  const { setExtras } = useContext(BreadcrumbContext);
  useEffect(() => {
    setExtras(crumbs || []);
    return () => setExtras([]);
    // We intentionally serialise `crumbs` so callers can pass inline arrays
    // without triggering infinite updates from reference equality.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(crumbs)]);
}

function CrumbSeparator() {
  return (
    <FiChevronRight size={12} aria-hidden className="shrink-0 text-fg-subtle" />
  );
}

/**
 * Breadcrumb — renders the shell's current trail.
 * Trail structure:   Section (label) › Nav page (link if not current) › Extras…
 */
export function Breadcrumb({ className }) {
  const location = useLocation();
  const { extras } = useContext(BreadcrumbContext);

  const trail = useMemo(() => {
    const out = [];
    const match = matchNavItem(location.pathname);
    if (match) {
      out.push({ label: match.item.sectionLabel, muted: true });
      out.push({
        label: match.item.label,
        to: match.isExact && extras.length === 0 ? undefined : match.item.path,
      });
    }
    for (const extra of extras) out.push(extra);
    return out;
  }, [location.pathname, extras]);

  if (trail.length === 0) return null;

  return (
    <nav
      aria-label="Breadcrumb"
      className={cn('flex items-center gap-1.5 text-caption min-w-0', className)}
    >
      {trail.map((crumb, i) => {
        const isLast = i === trail.length - 1;
        return (
          <React.Fragment key={i}>
            {i > 0 && <CrumbSeparator />}
            {crumb.to && !isLast ? (
              <Link
                to={crumb.to}
                className="text-fg-muted transition-colors hover:text-fg truncate"
              >
                {crumb.label}
              </Link>
            ) : (
              <span
                className={cn(
                  'truncate',
                  crumb.muted || !isLast ? 'text-fg-muted' : 'text-fg font-medium',
                )}
              >
                {crumb.label}
              </span>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}

export default Breadcrumb;
